/* global google */

import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import geohash from "ngeohash";

export const DEFAULT_GEO_BIN_CENTER = { lat: 39.8283, lng: -98.5795 };
export const DEFAULT_GEO_BIN_ZOOM = 12;
export const MIN_GEO_BIN_FETCH_ZOOM = 9;
export const MAX_GEO_BIN_FETCH_BINS = 250;
export const GEO_BIN_FETCH_DEBOUNCE_MS = 250;
export const MAX_GEO_BIN_MAP_ZOOM = 14;
export const GEO_BIN_VIEWPORT_QUERY_CHUNK = 10;
export const GEO_BIN_PRECISION = 4;
export const PRIVATE_GEO_BINS_COLLECTION = "geoBins4";
export const PRIVATE_GEO_BIN_SHARDS_COLLECTION = "geoBinMemberShards4";
export const PUBLIC_GEO_BINS_COLLECTION = "publicGeoBins4";
export const PUBLIC_GEO_BIN_SHARDS_COLLECTION = "publicGeoBinPointShards4";
export const GEO_BIN_MAP_ACCENT = "hsl(190,100%,30%)";
export const GEO_BIN_ACTIVE_ACCENT = "#2E7D32";
export const GEO_BIN_WAITLIST_ACCENT = "#C62828";
const DEMAND_MARKER_DIAMETER_SCALE = 0.7;
const DEMAND_CLUSTER_TEXT_SCALE = 0.85;
const scaleDemandDiameter = (size = 0) =>
  Math.max(1, Math.round(Number(size || 0) * DEMAND_MARKER_DIAMETER_SCALE));
const scaleDemandClusterText = (size = 0) =>
  Math.max(9, Math.round(Number(size || 0) * DEMAND_CLUSTER_TEXT_SCALE));

export const GEO_BIN_POINT_MARKER_SIZE = scaleDemandDiameter(40);
export const GEO_BIN_LOCATION_MARKER_SIZE = 42;
export const GEO_BIN_HOME_MARKER_SIZE = 36;
export const GEO_BIN_POINT_MARKER_SIZE_COMPACT = scaleDemandDiameter(30);
export const GEO_BIN_LOCATION_MARKER_SIZE_COMPACT = 34;
export const GEO_BIN_HOME_MARKER_SIZE_COMPACT = 30;

const GEO_BIN_CLUSTER_STYLE_COUNTS = [2, 10, 30];
const getDemandClusterBaseSize = (styleIndex = 0, compact = false) =>
  compact ? [44, 56, 68][styleIndex] : [56, 72, 88][styleIndex];
const getDemandClusterBaseTextSize = (styleIndex = 0, compact = false) =>
  compact ? [12, 14, 16][styleIndex] : [14, 17, 20][styleIndex];
const getDemandClusterStyleIndex = (count = 0) =>
  Number(count || 0) < 10 ? 0 : Number(count || 0) < 30 ? 1 : 2;

export const getDemandClusterBubbleSize = (
  count = 0,
  { compact = false } = {},
) =>
  scaleDemandDiameter(
    getDemandClusterBaseSize(getDemandClusterStyleIndex(count), compact),
  );

export const getDemandClusterTextSize = (
  count = 0,
  { compact = false } = {},
) =>
  scaleDemandClusterText(
    getDemandClusterBaseTextSize(getDemandClusterStyleIndex(count), compact),
  );

export const GEO_BIN_MAP_STYLE = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f7f5ef" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#d9ebf2" }],
  },
];

const PERSON_ICON_PATH =
  "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1c0 .55.45 1 1 1h14c.55 0 1-.45 1-1v-1c0-2.66-5.33-4-8-4z";
const HOME_ICON_PATH =
  "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z";

export const chunkList = (
  items = [],
  size = GEO_BIN_VIEWPORT_QUERY_CHUNK,
) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export const isSameCenter = (a, b) =>
  Math.abs(Number(a?.lat || 0) - Number(b?.lat || 0)) < 0.000001 &&
  Math.abs(Number(a?.lng || 0) - Number(b?.lng || 0)) < 0.000001;

export const isDefaultCenter = (center = DEFAULT_GEO_BIN_CENTER) =>
  Math.abs(Number(center?.lat || 0) - DEFAULT_GEO_BIN_CENTER.lat) < 0.001 &&
  Math.abs(Number(center?.lng || 0) - DEFAULT_GEO_BIN_CENTER.lng) < 0.001;

export const normalizeViewportBounds = (bounds) => {
  if (!bounds) return null;

  const north =
    typeof bounds.getNorthEast === "function"
      ? bounds.getNorthEast()?.lat?.()
      : bounds.north;
  const east =
    typeof bounds.getNorthEast === "function"
      ? bounds.getNorthEast()?.lng?.()
      : bounds.east;
  const south =
    typeof bounds.getSouthWest === "function"
      ? bounds.getSouthWest()?.lat?.()
      : bounds.south;
  const west =
    typeof bounds.getSouthWest === "function"
      ? bounds.getSouthWest()?.lng?.()
      : bounds.west;

  if (
    !Number.isFinite(Number(north)) ||
    !Number.isFinite(Number(east)) ||
    !Number.isFinite(Number(south)) ||
    !Number.isFinite(Number(west))
  ) {
    return null;
  }

  return {
    north: Number(north),
    east: Number(east),
    south: Number(south),
    west: Number(west),
  };
};

export const buildViewportKey = (viewport = {}) => {
  const bounds = normalizeViewportBounds(viewport?.bounds);
  if (!bounds) return "";

  return [
    Number(viewport?.zoom || 0).toFixed(2),
    bounds.north.toFixed(4),
    bounds.east.toFixed(4),
    bounds.south.toFixed(4),
    bounds.west.toFixed(4),
  ].join(":");
};

export const getViewportHashes = (
  bounds,
  zoom,
  precision = GEO_BIN_PRECISION,
) => {
  const normalizedBounds = normalizeViewportBounds(bounds);
  if (!normalizedBounds || Number(zoom || 0) < MIN_GEO_BIN_FETCH_ZOOM) return [];

  const { north, east, south, west } = normalizedBounds;
  if (east < west) return [];

  return Array.from(
    new Set(geohash.bboxes(south, west, north, east, precision)),
  );
};

const toSvgDataUrl = (svg) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const buildIconPathMarkup = ({
  path,
  size,
  x,
  y,
  fill = "#ffffff",
  opacity = 1,
}) =>
  `<g transform="translate(${x} ${y}) scale(${size / 24})" opacity="${opacity}"><path d="${path}" fill="${fill}" /></g>`;

export const buildSeniorMarkerSvg = (
  size = GEO_BIN_POINT_MARKER_SIZE,
  {
    fillColor = GEO_BIN_MAP_ACCENT,
    opacity = 1,
  } = {},
) =>
  toSvgDataUrl(`
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="${opacity}">
      <defs>
        <filter id="markerShadow" x="0" y="0" width="${size}" height="${size}" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="rgba(15,23,42,0.22)"/>
        </filter>
      </defs>
      <g filter="url(#markerShadow)">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.35625}" fill="${fillColor}" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.325}" stroke="rgba(255,255,255,0.88)" stroke-width="${size * 0.04375}" />
        ${buildIconPathMarkup({
          path: PERSON_ICON_PATH,
          size: size * 0.45,
          x: size * 0.275,
          y: size * 0.2625,
          fill: "#ffffff",
          opacity: 0.98,
        })}
      </g>
    </svg>
  `);

export const getDemandPointFillColor = (status = "") => {
  if (status === "active") return GEO_BIN_ACTIVE_ACCENT;
  if (status === "waitlist") return GEO_BIN_WAITLIST_ACCENT;
  return GEO_BIN_MAP_ACCENT;
};

export const buildLocationMarkerSvg = (
  size = GEO_BIN_LOCATION_MARKER_SIZE,
) =>
  toSvgDataUrl(`
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="locationShadow" x="0" y="0" width="${size}" height="${size}" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="rgba(15,23,42,0.18)"/>
        </filter>
      </defs>
      <g filter="url(#locationShadow)">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.3333}" fill="rgba(10,132,255,0.18)" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.2262}" fill="#0A84FF" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.1905}" stroke="rgba(255,255,255,0.96)" stroke-width="${size * 0.0714}" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.0762}" fill="rgba(255,255,255,0.96)" />
      </g>
    </svg>
  `);

export const buildHomeMarkerSvg = (
  size = GEO_BIN_HOME_MARKER_SIZE,
) =>
  toSvgDataUrl(`
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="homeShadow" x="0" y="0" width="${size}" height="${size}" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="2" stdDeviation="2.4" flood-color="rgba(15,23,42,0.35)"/>
        </filter>
      </defs>
      <g filter="url(#homeShadow)">
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.4444}" fill="white" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.3889}" fill="#007f99" />
        ${buildIconPathMarkup({
          path: HOME_ICON_PATH,
          size: size * 0.5,
          x: size * 0.1667,
          y: size * 0.1667,
          fill: "#ffffff",
          opacity: 0.98,
        })}
      </g>
    </svg>
  `);

export const buildClusterBubbleSvg = (
  size = 64,
  {
    fillColor = GEO_BIN_MAP_ACCENT,
    opacity = 1,
  } = {},
) => {
  const center = size / 2;
  const radius = size * 0.36;
  const strokeWidth = Math.max(1.5, size * 0.04);
  const shadowId = `clusterShadow${size}`;

  return toSvgDataUrl(`
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="${opacity}">
      <defs>
        <filter id="${shadowId}" x="0" y="0" width="${size}" height="${size}" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="rgba(15,23,42,0.22)"/>
        </filter>
      </defs>
      <g filter="url(#${shadowId})">
        <circle cx="${center}" cy="${center}" r="${radius}" fill="${fillColor}" />
        <circle cx="${center}" cy="${center}" r="${radius - strokeWidth / 2}" stroke="rgba(255,255,255,0.88)" stroke-width="${strokeWidth}" />
      </g>
    </svg>
  `);
};

export const buildGoogleMapIcon = ({
  kind = "senior",
  maps = google?.maps,
  status = "",
  colorByStatus = false,
  opacity = 1,
  compact = false,
}) => {
  if (!maps) return undefined;

  const size =
    kind === "location"
      ? compact
        ? GEO_BIN_LOCATION_MARKER_SIZE_COMPACT
        : GEO_BIN_LOCATION_MARKER_SIZE
      : kind === "home"
        ? compact
          ? GEO_BIN_HOME_MARKER_SIZE_COMPACT
          : GEO_BIN_HOME_MARKER_SIZE
        : compact
          ? GEO_BIN_POINT_MARKER_SIZE_COMPACT
          : GEO_BIN_POINT_MARKER_SIZE;
  const url =
    kind === "location"
      ? buildLocationMarkerSvg(size)
      : kind === "home"
        ? buildHomeMarkerSvg(size)
        : buildSeniorMarkerSvg(size, {
            fillColor: colorByStatus
              ? getDemandPointFillColor(status)
              : GEO_BIN_MAP_ACCENT,
            opacity,
          });

  return {
    url,
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
};

export const buildReactClusterOptions = ({
  titleFormatter = (count) => `${count} nearby senior members`,
  compact = false,
} = {}) => {
  const styles = GEO_BIN_CLUSTER_STYLE_COUNTS.map((count) => {
    const size = getDemandClusterBubbleSize(count, { compact });
    return {
      url: buildClusterBubbleSvg(size),
      width: size,
      height: size,
      textColor: "#ffffff",
      textSize: getDemandClusterTextSize(count, { compact }),
      fontWeight: "700",
      fontFamily: "Roboto, sans-serif",
    };
  });

  return {
    averageCenter: true,
    gridSize: 48,
    minimumClusterSize: 2,
    maxZoom: MAX_GEO_BIN_MAP_ZOOM,
    styles,
    calculator: (markers, numStyles) => {
      const count = markers.length;
      const index = count < 10 ? 1 : count < 30 ? 2 : numStyles;
      return {
        text: String(count),
        index,
        title: titleFormatter(count),
      };
    },
  };
};

export const buildImperativeClusterRenderer = ({
  titleFormatter = (count) => `${count} nearby senior members`,
  colorByStatus = false,
  opacity = 1,
  compact = false,
} = {}) => ({
  render({ count, position, markers = [] }) {
    const size = getDemandClusterBubbleSize(count, { compact });
    const activeCount = markers.filter(
      (marker) => marker?.geoBinStatus === "active",
    ).length;
    const waitlistCount = markers.filter(
      (marker) => marker?.geoBinStatus === "waitlist",
    ).length;
    const fillColor = colorByStatus
      ? activeCount > waitlistCount
        ? GEO_BIN_ACTIVE_ACCENT
        : GEO_BIN_WAITLIST_ACCENT
      : GEO_BIN_MAP_ACCENT;
    return new google.maps.Marker({
      position,
      clickable: false,
      zIndex: Number(google.maps.Marker.MAX_ZINDEX || 1000) + count,
      opacity,
      label: {
        text: String(count),
        color: "#ffffff",
        fontSize: `${getDemandClusterTextSize(count, { compact })}px`,
        fontWeight: "700",
      },
      icon: {
        url: buildClusterBubbleSvg(size, {
          fillColor,
          opacity,
        }),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
      title: titleFormatter(count),
    });
  },
});

export class SafeSuperClusterAlgorithm extends SuperClusterAlgorithm {
  calculate(input) {
    const zoom = input?.map?.getZoom?.();

    if (!Number.isFinite(zoom)) {
      return {
        clusters: this.noop({ markers: input?.markers || [] }),
        changed: true,
      };
    }

    return super.calculate(input);
  }
}

export const createSafeSuperClusterAlgorithm = (options = {}) =>
  new SafeSuperClusterAlgorithm({
    maxZoom: MAX_GEO_BIN_MAP_ZOOM,
    ...options,
  });

export const createSafeMarkerClusterer = ({
  map,
  markers = [],
  algorithmOptions = {},
  renderer,
  onClusterClick,
} = {}) =>
  new MarkerClusterer({
    map,
    markers,
    algorithm: createSafeSuperClusterAlgorithm(algorithmOptions),
    ...(renderer ? { renderer } : {}),
    ...(onClusterClick ? { onClusterClick } : {}),
  });

export const createImperativeClusterer = ({
  map,
  titleFormatter,
  colorByStatus = false,
  opacity = 1,
  compact = false,
} = {}) =>
  createSafeMarkerClusterer({
    map,
    markers: [],
    renderer: buildImperativeClusterRenderer({
      titleFormatter,
      colorByStatus,
      opacity,
      compact,
    }),
  });

export const buildPrivateGeoPointFromMemberEntry = (entry = {}) => ({
  pointId:
    String(entry?.memberId || "").trim() ||
    `${entry?.lat || 0}_${entry?.lng || 0}`,
  geoBinCountBucket: String(entry?.geoBinCountBucket || "").trim(),
  status:
    entry?.helperAvailability?.hasActiveHelpers === true ? "active" : "waitlist",
  city: String(entry?.city || "").trim(),
  state: String(entry?.state || "").trim(),
  stateCode: String(entry?.stateCode || "").trim(),
  lat: Number(entry?.lat || 0),
  lng: Number(entry?.lng || 0),
});

export const buildPointSignature = (points = []) =>
  points
    .map(
      (point) =>
        `${point?.pointId || ""}:${point?.status || ""}:${Number(
          point?.lat || 0,
        ).toFixed(4)}:${Number(point?.lng || 0).toFixed(4)}`,
    )
    .join("|");

export const createDemandPointMarker = ({
  point,
  clickable = false,
  zIndex = undefined,
  colorByStatus = false,
  opacity = 1,
  compact = false,
} = {}) =>
  Object.assign(
    new google.maps.Marker({
      position: {
        lat: Number(point?.lat || 0),
        lng: Number(point?.lng || 0),
      },
      clickable,
      optimized: true,
      opacity,
      zIndex,
      icon: buildGoogleMapIcon({
        kind: "senior",
        maps: google?.maps,
        status: point?.status,
        colorByStatus,
        opacity,
        compact,
      }),
    }),
    {
      geoBinStatus: point?.status || "",
    },
  );
